import React, { useState, useEffect, useCallback, useRef } from 'react'
import { FiSearch, FiX, FiPaperclip, FiAlertTriangle, FiArchive, FiInbox, FiTrash2, FiMail } from 'react-icons/fi'
import { MdClose } from 'react-icons/md'
import EmailFrame from './EmailFrame'
import { StatusText, confirmDelete, toast, SelectionBar } from './EmailLogPanel'

const formatTime = (value) => new Date(value).toLocaleString(undefined, {
  month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
})

const formatSize = (bytes = 0) => (bytes > 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`)

const VIEWS = [['inbox', 'Inbox'], ['unread', 'Unread'], ['archived', 'Archived']]

// what a sent reply said, from the copy kept in the log
const replyText = (log) => (log.content?.paragraphs || [])
  .map(p => p.replace(/<br>/g, '\n').replace(/<[^>]+>/g, '')
    .replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&amp;/g, '&'))
  .join('\n\n')

// One received email, our replies under it, and the reply box
const ThreadModal = ({ id, route, adminFetch, onClose, onChange }) => {
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [reply, setReply] = useState('')
  const [sending, setSending] = useState(false)

  const load = useCallback(async () => {
    try {
      const res = await (await adminFetch(`${route}/api/admin/inbox/${id}`)).json()
      if (res.status !== 200) throw new Error(res.message)
      setData(res)
    } catch (e) {
      setError(e.message || 'Could not load the email')
    }
  }, [id]) // eslint-disable-line react-hooks/exhaustive-deps

  // opening marks it read on the server; refresh the list behind
  useEffect(() => { load().then(onChange) }, [load]) // eslint-disable-line react-hooks/exhaustive-deps

  const update = async (body, message) => {
    const res = await (await adminFetch(`${route}/api/admin/inbox/${id}`, { method: 'PATCH', body: JSON.stringify(body) })).json()
    if (res.status !== 200) return toast('error', res.message || 'Could not update')
    toast('success', message)
    onChange()
    onClose()
  }

  const remove = async () => {
    if (!(await confirmDelete(1))) return
    const res = await (await adminFetch(`${route}/api/admin/inbox/${id}`, { method: 'DELETE' })).json()
    if (res.status !== 200) return toast('error', res.message || 'Could not delete')
    toast('success', 'Email deleted')
    onChange()
    onClose()
  }

  const openAttachment = async (attachment) => {
    // open the tab first: browsers block window.open after an await
    const tab = window.open('', '_blank')
    const res = await (await adminFetch(`${route}/api/admin/inbox/${id}/attachments/${attachment.id}`)).json()
    if (res.status !== 200 || !res.url) {
      tab?.close()
      return toast('error', res.message || 'Could not open the attachment')
    }
    if (tab) tab.location.href = res.url
    else window.location.href = res.url
  }

  const sendReply = async () => {
    if (!reply.trim()) return
    setSending(true)
    try {
      const res = await (await adminFetch(`${route}/api/admin/inbox/${id}/reply`, {
        method: 'POST', body: JSON.stringify({ message: reply }),
      })).json()
      if (res.status !== 200) throw new Error(res.message)
      toast('success', 'Reply sent')
      setReply('')
      load()
      onChange()
    } catch (e) {
      toast('error', e.message || 'Could not send the reply')
    } finally {
      setSending(false)
    }
  }

  const email = data?.email

  return (
    <div className="adm-modal-overlay" onClick={onClose}>
      <div className="adm-modal xwide scroll" onClick={e => e.stopPropagation()}>
        <button className="adm-modal-close" onClick={onClose} aria-label="Close"><MdClose size={16} /></button>
        {!email && <p className="adm-modal-sub">{error || 'Loading…'}</p>}
        {email && (
          <>
            <p className="adm-modal-title">{email.subject || '(no subject)'}</p>
            <p className="adm-modal-sub">
              From <strong>{email.fromName ? `${email.fromName} <${email.fromAddress}>` : email.fromAddress}</strong>
              {' · '}{formatTime(email.receivedAt)}
            </p>

            {data.user && (
              <div className="adm-detail-row">
                <span>Account</span>
                <strong>{data.user.firstname} {data.user.lastname} (@{data.user.username})</strong>
              </div>
            )}
            <div className="adm-detail-row"><span>To</span><strong>{[...email.to, ...email.cc].join(', ')}</strong></div>
            {email.replyTo && email.replyTo !== email.fromAddress && (
              <div className="adm-detail-row"><span>Replies go to</span><strong>{email.replyTo}</strong></div>
            )}
            {email.spam && (
              <div className="adm-email-error">
                <FiAlertTriangle size={13} /> The sender's domain couldn't be verified (SPF, DKIM and DMARC all failed). This may be spoofed or spam.
              </div>
            )}

            <div className="adm-email-body">
              <EmailFrame html={email.html} text={email.text} light />
            </div>

            {email.attachments?.length > 0 && (
              <div className="adm-attachments">
                {email.attachments.map(a => (
                  <button key={a.id} className="adm-attachment" onClick={() => openAttachment(a)}>
                    <FiPaperclip size={13} />
                    <span>{a.filename || 'attachment'}</span>
                    <span className="adm-user-meta">{formatSize(a.size)}</span>
                  </button>
                ))}
              </div>
            )}

            {data.replies.length > 0 && <p className="adm-detail-heading">Your replies</p>}
            {data.replies.map(log => (
              <div key={log._id} className="adm-reply">
                <p className="adm-reply-meta">
                  {log.sentBy || 'Admin'} · {formatTime(log.createdAt)} · <StatusText status={log.status} />
                  {log.openedAt && ' · Opened'}
                </p>
                <p className="adm-reply-text">{replyText(log)}</p>
                {log.error && <div className="adm-email-error">{log.error}</div>}
              </div>
            ))}

            <p className="adm-detail-heading">Reply to {email.replyTo || email.fromAddress}</p>
            <textarea
              className="adm-form-input adm-form-textarea"
              rows={6}
              value={reply}
              placeholder="Write your reply. Their message is quoted below it automatically."
              onChange={e => setReply(e.target.value)}
            />

            <div className="adm-modal-actions adm-thread-actions">
              <button className="adm-btn ghost small" onClick={() => update({ archived: !email.archived }, email.archived ? 'Moved to inbox' : 'Archived')}>
                {email.archived ? <><FiInbox size={14} /> Move to inbox</> : <><FiArchive size={14} /> Archive</>}
              </button>
              <button className="adm-btn ghost small" onClick={() => update({ read: false }, 'Marked as unread')}>
                <FiMail size={14} /> Mark unread
              </button>
              <button className="adm-btn danger small" onClick={remove}><FiTrash2 size={14} /> Delete</button>
              <button className="adm-btn primary" disabled={sending || !reply.trim()} onClick={sendReply}>
                {sending ? 'Sending…' : 'Send reply'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// "Inbox" tab: email received through Resend, newest first
const InboxPanel = ({ route, adminFetch, onChange }) => {
  const [emails, setEmails] = useState([])
  const [unread, setUnread] = useState(0)
  const [inboundAddress, setInboundAddress] = useState('')
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [openId, setOpenId] = useState(null)
  const [selected, setSelected] = useState(() => new Set())
  const [deleting, setDeleting] = useState(false)

  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const [view, setView] = useState('inbox')

  const fetchRef = useRef(adminFetch)
  fetchRef.current = adminFetch

  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), 300)
    return () => clearTimeout(timer)
  }, [search])

  const load = useCallback(async (before) => {
    setLoading(true)
    setError('')
    try {
      const params = new URLSearchParams({ view, search: query })
      if (before) params.set('before', before)
      const res = await (await fetchRef.current(`${route}/api/admin/inbox?${params}`)).json()
      if (res.status !== 200) throw new Error(res.message || 'Could not load the inbox')
      setEmails(prev => (before ? [...prev, ...res.emails] : res.emails))
      if (!before) setSelected(new Set())
      setHasMore(res.hasMore)
      setUnread(res.unread)
      setInboundAddress(res.inboundAddress)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [route, view, query])

  useEffect(() => { load() }, [load])

  const refresh = () => { load(); onChange?.() }

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })
  const allSelected = emails.length > 0 && emails.every(e => selected.has(e._id))
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(emails.map(e => e._id)))

  const deleteSelected = async () => {
    if (!(await confirmDelete(selected.size))) return
    setDeleting(true)
    try {
      const res = await (await fetchRef.current(`${route}/api/admin/inbox/delete`, {
        method: 'POST', body: JSON.stringify({ ids: [...selected] }),
      })).json()
      if (res.status !== 200) throw new Error(res.message)
      toast('success', `${res.deleted} email${res.deleted === 1 ? '' : 's'} deleted`)
      refresh()
    } catch (e) {
      toast('error', e.message || 'Could not delete')
    } finally {
      setDeleting(false)
    }
  }

  return (
    <>
      <div className="adm-toolbar">
        <div className="adm-search">
          <FiSearch className="adm-search-icon" />
          <input
            type="search"
            placeholder="Search by sender or subject…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button className="adm-search-clear" onClick={() => setSearch('')} aria-label="Clear search"><FiX size={14} /></button>
          )}
        </div>
      </div>

      <div className="adm-chips">
        {VIEWS.map(([value, label]) => (
          <button key={value} className={`adm-chip ${view === value ? 'active' : ''}`} onClick={() => setView(value)}>
            {label}{value === 'unread' && unread > 0 && <span>{unread}</span>}
          </button>
        ))}
      </div>

      {inboundAddress && (
        <p className="adm-result-count">Email sent to {inboundAddress} arrives here. Replies to the site's emails go there too.</p>
      )}

      {error && <div className="adm-empty"><p>{error}</p></div>}

      <SelectionBar count={selected.size} busy={deleting} onDelete={deleteSelected} onClear={() => setSelected(new Set())} />

      {!error && emails.length > 0 && (
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead>
              <tr>
                <th className="adm-check-cell">
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} aria-label="Select all shown" />
                </th>
                <th>From</th>
                <th>Subject</th>
                <th>Received</th>
              </tr>
            </thead>
            <tbody>
              {emails.map(email => (
                <tr key={email._id} className={`adm-email-row ${email.read ? '' : 'unread'} ${selected.has(email._id) ? 'selected' : ''}`} onClick={() => setOpenId(email._id)}>
                  <td className="adm-check-cell" onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={selected.has(email._id)} onChange={() => toggle(email._id)} aria-label={`Select email from ${email.fromAddress}`} />
                  </td>
                  <td>
                    <span className="adm-user-name">{email.fromName || email.fromAddress}</span>
                    {email.fromName && <span className="adm-user-meta">{email.fromAddress}</span>}
                  </td>
                  <td className="adm-inbox-subject">
                    <span className="adm-inbox-subject-line">
                      {email.spam && <FiAlertTriangle size={12} className="adm-inbox-flag" title="Sender not verified" />}
                      {email.attachments?.length > 0 && <FiPaperclip size={12} className="adm-inbox-flag" title="Has attachments" />}
                      {email.subject || '(no subject)'}
                    </span>
                    {email.snippet && <span className="adm-user-meta">{email.snippet}</span>}
                  </td>
                  <td><span className="adm-user-meta">{formatTime(email.receivedAt)}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!error && !loading && emails.length === 0 && (
        <div className="adm-empty">
          {query || view !== 'inbox'
            ? <p>No emails here.</p>
            : inboundAddress
              ? <p>No email received yet. Messages sent to {inboundAddress} will show up here.</p>
              : <p>Receiving isn't set up yet. Turn on receiving for your domain in Resend, add the "email.received" webhook, and set INBOUND_EMAIL on the server.</p>}
        </div>
      )}

      {hasMore && (
        <div className="adm-load-more">
          <button className="adm-btn ghost" disabled={loading} onClick={() => load(emails[emails.length - 1]._id)}>
            {loading ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}

      {openId && (
        <ThreadModal id={openId} route={route} adminFetch={adminFetch} onClose={() => setOpenId(null)} onChange={refresh} />
      )}
    </>
  )
}

export default InboxPanel
